import { useRollbar } from '@rollbar/react'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { type ReactCropperElement } from 'react-cropper'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { ModalWindow } from 'src/editor/shared/ui-lib/modal-window'
import { Toggle } from 'src/editor/shared/ui-lib/toggle/toggle'
import 'cropperjs/dist/cropper.css'

const Cropper = React.lazy(() => import('react-cropper'))

export type ImageCropperType = Omit<
  React.ComponentPropsWithoutRef<typeof Cropper>,
  'src'
>

export interface ImageCropperProps {
  imageFile: File | undefined
  isOpened: boolean
  onClose: () => void
  onCropComplete: (blobUrl: string, blob: Blob) => void
  imageCropperProps?: ImageCropperType
  isRounded?: boolean
}

export const ImageCropper = ({
  imageCropperProps,
  imageFile,
  isOpened,
  onClose,
  onCropComplete,
  isRounded,
}: ImageCropperProps) => {
  const {
    viewMode = 1,
    autoCropArea = 1,
    aspectRatio = 1,
    checkOrientation = false,
    background = true,
    dragMode = 'move',
    toggleDragModeOnDblclick = false,
    style = { height: 400, width: '100%' },
    ...cropperPropsRest
  } = imageCropperProps || {}

  const { t } = useTranslation()
  const rollbar = useRollbar()
  const cropperRef = useRef<ReactCropperElement | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [cropError, setCropError] = useState<string | null>(null)
  const [isPreviewEnabled, setIsPreviewEnabled] = useState(false)
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null)

  const imageSrc = useMemo(
    () => (imageFile ? URL.createObjectURL(imageFile) : undefined),
    [imageFile],
  )

  useEffect(() => {
    return () => {
      if (imageSrc) {
        URL.revokeObjectURL(imageSrc)
      }
    }
  }, [imageSrc])

  const updatePreviewImage = () => {
    const cropper = cropperRef.current?.cropper

    if (typeof cropper === 'undefined') {
      return false
    }

    const canvas = cropper.getCroppedCanvas()

    if (!canvas) {
      return false
    }

    setPreviewImageUrl(canvas.toDataURL())
    return true
  }

  const handleApply = async () => {
    try {
      if (typeof cropperRef.current?.cropper === 'undefined') {
        return
      }

      setIsLoading(true)
      setCropError(null)

      const originalFile = imageFile as File
      const canvas = cropperRef.current.cropper.getCroppedCanvas()

      if (!canvas) {
        rollbar.error('Failed to get cropped canvas', {
          fileName: imageFile?.name,
          fileSize: imageFile?.size,
        })
      }

      const blob: Blob = await new Promise((resolve, reject) => {
        const outputType = originalFile?.type || 'image/png'
        canvas.toBlob(
          (b: Blob | null) =>
            b ? resolve(b) : reject(new Error(t('image_uploader.crop_failed'))),
          outputType,
        )
      })

      const blobUrl = URL.createObjectURL(blob)
      onCropComplete(blobUrl, blob)
      onClose()
    } catch (e) {
      rollbar.error('Image crop failed', {
        error:
          e instanceof Error
            ? { name: e.name, message: e.message, stack: e.stack }
            : e,
        fileName: imageFile?.name,
        fileSize: imageFile?.size,
      })

      const message =
        e instanceof Error && typeof e.message === 'string'
          ? e.message.trim()
          : t('global.error')

      setCropError(message)
      toast.error(message)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <ModalWindow
      isOpened={isOpened}
      onClose={() => {
        setCropError(null)
        setIsPreviewEnabled(false)
        setPreviewImageUrl(null)
        onClose()
      }}
      title={t('global.upload_image')}
      cancelButtonProps={{
        onClick: () => {
          setCropError(null)
          setIsPreviewEnabled(false)
          setPreviewImageUrl(null)
          onClose()
        },
        children: t('global.cancel'),
      }}
      confirmButtonProps={{
        onClick: handleApply,
        isLoading,
        children: t('global.click_to_upload'),
      }}
    >
      <div className="relative w-full overflow-hidden">
        <div className={isPreviewEnabled ? 'invisible' : undefined}>
          <React.Suspense fallback={<div style={style} />}>
            <Cropper
              ref={cropperRef}
              src={imageSrc}
              style={style}
              viewMode={viewMode}
              background={background}
              autoCropArea={autoCropArea}
              aspectRatio={aspectRatio}
              checkOrientation={checkOrientation}
              dragMode={dragMode}
              toggleDragModeOnDblclick={toggleDragModeOnDblclick}
              {...cropperPropsRest}
            />
          </React.Suspense>
        </div>
        {isPreviewEnabled && (
          <div
            data-is-rounded={isRounded}
            className="absolute inset-0 flex h-[400px] w-full items-center justify-center overflow-hidden bg-bgSecondary data-[is-rounded=true]:rounded-[50%]"
          >
            {previewImageUrl && (
              <img
                src={previewImageUrl}
                alt="Cropped preview"
                className="max-h-full max-w-full object-contain"
              />
            )}
          </div>
        )}
      </div>
      <Toggle
        wrapperClassName="mt-3"
        checked={isPreviewEnabled}
        onChange={checked => {
          if (checked) {
            const hasPreview = updatePreviewImage()
            if (!hasPreview) {
              toast.error(t('image_uploader.crop_failed'))
              setIsPreviewEnabled(false)
              return
            }
          }
          setIsPreviewEnabled(checked)
        }}
        label="Preview"
        skipTranslation
      />
      {cropError && (
        <div className="mt-2 text-center text-S text-error-500">
          {cropError}
        </div>
      )}
    </ModalWindow>
  )
}
